import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  RESILIATION_PF_MAIL_SUBJECT,
  RESILIATION_PF_MAIL_BODY,
  getResiliationPfMailPlainText,
} from '@/data/mailResiliationAssurance';

export default function PostPaymentResiliationDialog({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const text = getResiliationPfMailPlainText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Courrier copié — collez-le dans votre messagerie (objet + texte).');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copie impossible. Autorisez le presse-papiers ou sélectionnez le texte manuellement.');
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[min(90vh,760px)] flex flex-col gap-4 rounded-none sm:rounded-none p-6"
        data-testid="post-payment-resiliation-dialog"
      >
        <DialogHeader className="shrink-0 text-left space-y-2">
          <DialogTitle className="text-neutral-950 pr-8 font-heading">
            Paiement confirmé — prochaine étape
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-sm text-left">
            Pour l&apos;assurance perte financière de votre contrat, vous pouvez envoyer le courrier ci-dessous à votre
            assureur. Copiez le texte puis collez-le dans un nouvel e-mail (l&apos;objet est inclus).
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 flex flex-col border border-neutral-200 bg-neutral-50/80">
          <div className="shrink-0 border-b border-neutral-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 mb-0.5">Objet</p>
            <p className="text-sm font-medium text-neutral-900 leading-snug">{RESILIATION_PF_MAIL_SUBJECT}</p>
          </div>
          <pre
            className="text-xs text-neutral-800 whitespace-pre-wrap font-sans p-3 overflow-y-auto max-h-[min(42vh,320px)] leading-relaxed"
            data-testid="post-payment-mail-body"
          >
            {RESILIATION_PF_MAIL_BODY}
          </pre>
        </div>

        <DialogFooter className="shrink-0 flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-none w-full sm:w-auto border-neutral-300"
            onClick={() => onOpenChange?.(false)}
          >
            Fermer
          </Button>
          <Button
            type="button"
            className="rounded-none w-full sm:w-auto bg-neutral-950 text-white hover:bg-neutral-800"
            onClick={handleCopy}
            data-testid="post-payment-copy-mail-btn"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            Copier pour envoyer par mail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
